import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarDays, BarChart3, Inbox, LogOut, Sprout, CalendarClock, Settings2, Images, Camera } from "lucide-react";
import EventsTab from "./EventsTab";
import StatsTab from "./StatsTab";
import ListsTab from "./ListsTab";
import CalendarTab from "./CalendarTab";
import SettingsTab from "./SettingsTab";
import PastEventsTab from "./PastEventsTab";
import ProgramGalleryTab from "./ProgramGalleryTab";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const adminApi = () => {
  const token = localStorage.getItem("lfo_admin_token");
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
};

const TABS = [
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "calendar", label: "Pantry Calendar", icon: CalendarClock },
  { id: "settings", label: "Pantry Settings", icon: Settings2 },
  { id: "stats", label: "Impact Numbers", icon: BarChart3 },
  { id: "gallery", label: "Program Photos", icon: Images },
  { id: "pastevents", label: "Past Events", icon: Camera },
  { id: "inbox", label: "Messages & Donations", icon: Inbox },
];

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("events");
  const [overview, setOverview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("lfo_admin_token");
    if (!token) { navigate("/admin/login"); return; }
    adminApi().get(`${API}/auth/me`)
      .then((r) => {
        setUser(r.data);
        return adminApi().get(`${API}/admin/overview`);
      })
      .then((r) => setOverview(r.data))
      .catch(() => { localStorage.removeItem("lfo_admin_token"); navigate("/admin/login"); });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("lfo_admin_token");
    navigate("/admin/login");
  };

  if (!user) {
    return <main className="min-h-screen flex items-center justify-center text-sm text-[#5C7368]" data-testid="admin-loading">Loading admin…</main>;
  }

  return (
    <main className="min-h-screen pt-24 pb-20 px-6" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-[#C4D7D1] flex items-center justify-center">
              <Sprout size={20} className="text-[#1E352A]" />
            </span>
            <div>
              <h1 className="font-display text-3xl text-[#1A2F24]">Website Admin</h1>
              <p className="text-xs tracking-[0.2em] uppercase text-[#5C7368]">Logged in as {user.username}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#1E352A]/25 px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-[#1E352A] transition-colors duration-300 hover:border-red-400 hover:text-red-700">
            <LogOut size={14} /> Log Out
          </button>
        </div>

        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10" data-testid="admin-overview">
            {[
              ["Messages", overview.contacts],
              ["Volunteers", overview.volunteers],
              ["Events", overview.events],
              ["Pantry Appts", overview.appointments],
              ["Donations", overview.donations_count],
              ["Raised", `$${overview.donations_total.toLocaleString()}`],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-[#E2E8E4] bg-[#F2F4F0] p-5">
                <p className="font-display text-3xl text-[#1A2F24]">{value}</p>
                <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#5C7368]">{label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs tracking-[0.12em] uppercase transition-all duration-300 ${
                tab === t.id ? "bg-[#1E352A] text-[#FAF9F6]" : "bg-[#F2F4F0] border border-[#E2E8E4] text-[#5C7368] hover:border-[#F4A261]"
              }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "events" && <EventsTab />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "stats" && <StatsTab />}
        {tab === "gallery" && <ProgramGalleryTab />}
        {tab === "pastevents" && <PastEventsTab />}
        {tab === "inbox" && <ListsTab />}
      </div>
    </main>
  );
};

export default AdminDashboard;
